import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Archive,
  BookOpen,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Database,
  Eye,
  FolderCog,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  User,
  type LucideIcon,
} from 'lucide-react';
import KnowledgebaseManagementModal from './KnowledgebaseManagementModal';
import KnowledgeEntityModal from './KnowledgeEntityModal';
import { ingestionService } from '../../../../components/api/ingestionService';
import { dataVaultService } from '../../../../components/api/dataVaultService';
import { embeddingProviderService, type EmbeddingProvider } from '../../../../components/api/embeddingProviderService';
import { getUserInfo } from '../../../../routes/decodedAdmin';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import RenameModal from '../../common/RenameModal';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
import { useTheme } from '../../../../components/common/Theme';
import EmptyState from '../../governed-mcp-gateway/components/EmptyState';
import { fieldClass, primaryBtn } from '../../governed-mcp-gateway/components/GatewayUi';

interface Entity {
    id: string;
    name: string;
}

interface KnowledgeWorkspace {
    id: string;
    tenantId: string;
    title: string;
    description: string;
    tags: string[];
    createdBy: string;
    createdOn: string;
    vectorSore?: string;
    embeddingModel?: string;
    vaultId?: string;
    destinationType?: string;
    destination?: string | null;
}

type DestFilter = 'all' | 'vector' | 'vault' | 'other';
type NameSort = 'name-asc' | 'name-desc';

const PAGE_SIZE = 12;

function destinationOf(workspace: KnowledgeWorkspace): Exclude<DestFilter, 'all'> {
    if (workspace.vectorSore) return 'vector';
    if (workspace.vaultId) return 'vault';
    return 'other';
}

function formatCreatedOn(value: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 2000) return '—';
    return date.toLocaleString();
}

function displayOrDash(value?: string | null): string {
    const text = (value || '').trim();
    return text || '—';
}

function asList(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) {
        return raw.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
    }
    if (raw && typeof raw === 'object') {
        const rec = raw as Record<string, unknown>;
        return asList(rec.data ?? rec.Data ?? rec.items ?? rec.Items);
    }
    return [];
}

function pickText(row: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
        const value = row[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function pickId(row: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
        const value = row[key];
        if (value == null || value === '') continue;
        const text = String(value).trim();
        if (text && text.toLowerCase() !== '00000000-0000-0000-0000-000000000000') return text;
    }
    return '';
}

function embeddingOptionLabel(provider: EmbeddingProvider): string {
    const display = provider.displayName?.trim() || provider.name?.trim() || 'Embedding provider';
    const model = provider.defaultModel?.trim();
    return model ? `${display} · ${model}` : display;
}

function mapWorkspaceRow(row: Record<string, unknown>): KnowledgeWorkspace {
    const tagsRaw = row.tags ?? row.Tags;
    const tags = Array.isArray(tagsRaw) ? tagsRaw.map((tag) => String(tag ?? '').trim()).filter(Boolean) : [];
    return {
        id: pickId(row, 'id', 'Id'),
        tenantId: pickId(row, 'tenantId', 'TenantId'),
        title: pickText(row, 'title', 'Title'),
        description: pickText(row, 'description', 'Description'),
        tags,
        createdBy: pickText(row, 'createdBy', 'CreatedBy') || 'Unknown',
        createdOn: pickText(row, 'createdOn', 'CreatedOn', 'createdAt', 'CreatedAt'),
        vectorSore: pickText(row, 'vectorSore', 'VectorSore'),
        embeddingModel: pickText(row, 'embeddingModel', 'EmbeddingModel'),
        vaultId: pickText(row, 'vaultId', 'VaultId'),
        destinationType: pickText(row, 'destinationType', 'DestinationType'),
        destination: pickText(row, 'destination', 'Destination') || null,
    };
}

function OverviewRow({
    icon: Icon,
    label,
    value,
    isLight,
}: {
    icon: LucideIcon;
    label: string;
    value: string;
    isLight: boolean;
}) {
    return (
        <div className="flex items-start gap-2 min-w-0">
            <Icon size={12} className={isLight ? 'text-neutral-400 mt-0.5 shrink-0' : 'text-neutral-500 mt-0.5 shrink-0'} />
            <div className="min-w-0 flex-1">
                <div className={`text-[10px] uppercase tracking-wide ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{label}</div>
                <div className={`text-[11px] leading-snug break-words whitespace-normal ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`} title={value}>
                    {value}
                </div>
            </div>
        </div>
    );
}

function WorkspaceCard({
    workspace,
    isLight,
    overviewOpen,
    onOpen,
    onOverview,
    onRename,
    onDelete,
}: {
    workspace: KnowledgeWorkspace;
    isLight: boolean;
    overviewOpen: boolean;
    onOpen: () => void;
    onOverview: () => void;
    onRename: () => void;
    onDelete: () => void;
}) {
    const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
    const iconBtn = isLight
        ? 'p-1.5 rounded-lg text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50'
        : 'p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700';
    const chips = [
        workspace.destinationType?.trim() || 'Workspace',
        ...((workspace.tags || []).filter(Boolean)),
    ].slice(0, 3);
    const extraTags = Math.max(0, (workspace.tags || []).length - Math.max(0, chips.length - 1));

    return (
        <article
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                }
            }}
            className={`group relative ${overviewOpen ? 'z-20' : 'z-0 hover:z-10'} flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
                isLight
                    ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
                    : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
            }`}
        >
            <div className="flex items-center gap-3 min-w-0 pr-16">
                <span
                    className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                        isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
                    }`}
                >
                    <BookOpen size={18} />
                </span>
                <h3
                    className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}
                    title={workspace.title}
                >
                    {workspace.title}
                </h3>
            </div>

            <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${muted}`}>
                {workspace.description || 'No description provided.'}
            </p>
            <p className={`mt-2 text-[11px] truncate ${muted}`} title={`${workspace.createdBy || 'Unknown'} · ${formatCreatedOn(workspace.createdOn)}`}>
                {workspace.createdBy || 'Unknown'} · {formatCreatedOn(workspace.createdOn)}
            </p>

            <div className={`mt-4 pt-4 border-t flex flex-wrap gap-1.5 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
                {chips.map((chip) => (
                    <span
                        key={chip}
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium max-w-[140px] truncate ${
                            isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
                        }`}
                        title={chip}
                    >
                        {chip}
                    </span>
                ))}
                {extraTags > 0 ? (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] ${muted}`}>+{extraTags}</span>
                ) : null}
                {workspace.vectorSore ? (
                    <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium max-w-[160px] ${
                            isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                        }`}
                        title={workspace.vectorSore}
                    >
                        <Database size={11} />
                        <span className="truncate">{workspace.vectorSore}</span>
                    </span>
                ) : null}
                {workspace.vaultId ? (
                    <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium max-w-[160px] ${
                            isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300'
                        }`}
                        title={workspace.vaultId}
                    >
                        <Archive size={11} />
                        <span className="truncate">{workspace.vaultId}</span>
                    </span>
                ) : null}
            </div>

            <div
                data-menu-root={`overview-${workspace.id}`}
                className={`absolute top-3.5 right-3.5 ${overviewOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto'} transition-opacity`}
            >
                <div
                    className={`flex items-center gap-0.5 rounded-xl p-0.5 ${
                        isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
                    }`}
                >
                <button
                    type="button"
                    className={iconBtn}
                    aria-label="Overview"
                    title="Overview"
                    onClick={(event) => {
                        event.stopPropagation();
                        onOverview();
                    }}
                >
                    <Eye size={14} />
                </button>
                <button
                    type="button"
                    className={iconBtn}
                    aria-label="Manage"
                    title="Manage"
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpen();
                    }}
                >
                    <FolderCog size={14} />
                </button>
                <button
                    type="button"
                    className={iconBtn}
                    aria-label="Rename"
                    title="Edit Source"
                    onClick={(event) => {
                        event.stopPropagation();
                        onRename();
                    }}
                >
                    <Pencil size={14} />
                </button>
                <button
                    type="button"
                    className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
                    aria-label="Delete"
                    title="Delete Workspace"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDelete();
                    }}
                >
                    <Trash2 size={14} />
                </button>
                </div>
                <Menu
                    open={overviewOpen}
                    onClose={onOverview}
                    isLight={isLight}
                    align="right"
                    root={`overview-${workspace.id}`}
                >
                    <div className="px-3 py-2.5 w-[280px] space-y-2.5" onClick={(event) => event.stopPropagation()}>
                        <OverviewRow icon={Database} label="Vector store" value={displayOrDash(workspace.vectorSore)} isLight={isLight} />
                        <OverviewRow icon={Layers} label="Embedding model" value={displayOrDash(workspace.embeddingModel)} isLight={isLight} />
                        <OverviewRow icon={Archive} label="Data vault" value={displayOrDash(workspace.vaultId)} isLight={isLight} />
                        <OverviewRow icon={User} label="Created by" value={displayOrDash(workspace.createdBy)} isLight={isLight} />
                        <OverviewRow icon={Clock} label="Created on" value={formatCreatedOn(workspace.createdOn)} isLight={isLight} />
                    </div>
                </Menu>
            </div>
        </article>
    );
}

function Menu({
    open,
    onClose,
    children,
    isLight,
    align = 'right',
    root,
}: {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    isLight: boolean;
    align?: 'left' | 'right';
    root: string;
}) {
    useEffect(() => {
        if (!open) return;
        const onPointer = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest(`[data-menu-root="${root}"]`)) return;
            onClose();
        };
        document.addEventListener('mousedown', onPointer);
        return () => document.removeEventListener('mousedown', onPointer);
    }, [open, onClose, root]);
    if (!open) return null;
    return (
        <div
            onClick={(event) => event.stopPropagation()}
            className={`absolute z-20 mt-1.5 min-w-[200px] rounded-xl border py-1 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'} ${
                isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700'
            }`}
        >
            {children}
        </div>
    );
}

const DataSourceListingPage: React.FC = () => {
    const { theme } = useTheme();
    const isLight = theme === 'white';
    const navigate = useNavigate();
    const { workspaceId: routeWorkspaceId, kbId: routeKbId } = useParams<{ workspaceId?: string; kbId?: string }>();
    const [searchParams] = useSearchParams();
    const deepLinkWorkspaceId = useMemo(() => {
        const fromRoute = routeWorkspaceId?.trim();
        if (fromRoute) return fromRoute;
        return (
            searchParams.get('workspaceId')?.trim()
            || searchParams.get('knowledgeBaseId')?.trim()
            || null
        );
    }, [routeWorkspaceId, searchParams]);
    const deepLinkKbId = useMemo(() => {
        const fromRoute = routeKbId?.trim();
        if (fromRoute) return fromRoute;
        return searchParams.get('kbId')?.trim() || null;
    }, [routeKbId, searchParams]);
    const openedDeepLinkRef = useRef<string | null>(null);

    const [search, setSearch] = useState('');
    const [destFilter, setDestFilter] = useState<DestFilter>('all');
    const [sort, setSort] = useState<NameSort>('name-asc');
    const [page, setPage] = useState(1);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const [workspaces, setWorkspaces] = useState<KnowledgeWorkspace[]>([]);
    const [loading, setLoading] = useState(true);
    const [showEntityModal, setShowEntityModal] = useState(false);
    const [selectedWorkspace, setSelectedWorkspace] = useState<KnowledgeWorkspace | null>(null);
    const [showKBModal, setShowKBModal] = useState(false);
    const [workspaceToDelete, setWorkspaceToDelete] = useState<KnowledgeWorkspace | null>(null);
    const [overviewId, setOverviewId] = useState<string | null>(null);
    const workspacesRef = useRef<KnowledgeWorkspace[]>([]);
    workspacesRef.current = workspaces;

    useEffect(() => {
        openedDeepLinkRef.current = null;
    }, [deepLinkWorkspaceId]);

    useEffect(() => {
        fetchWorkspaces();
    }, []);
    const fetchWorkspaces = async () => {
        setLoading(true);
        try {
            const response = await ingestionService.loadkbEntitiesList();
            setWorkspaces(asList(response).map(mapWorkspaceRow).filter((row) => row.id));
        } catch (err) {
            console.error('Failed to fetch workspaces', err);
        } finally {
            setLoading(false);
        }
    };
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const next = workspaces.filter((workspace) => {
            if (destFilter !== 'all' && destinationOf(workspace) !== destFilter) return false;
            if (!q) return true;
            return workspace.title.toLowerCase().includes(q);
        });
        return [...next].sort((a, b) => {
            const cmp = a.title.localeCompare(b.title);
            return sort === 'name-desc' ? -cmp : cmp;
        });
    }, [destFilter, search, sort, workspaces]);

    useEffect(() => {
        setPage(1);
    }, [search, destFilter, sort]);

    useEffect(() => {
        if (!overviewId) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOverviewId(null);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [overviewId]);

    useEffect(() => {
        if (!overviewId) return;
        const current = workspacesRef.current.find((item) => item.id === overviewId);
        if (!current) return;
        const needsEmbedding = !current.embeddingModel?.trim();
        const needsVault = !current.vaultId?.trim();
        if (!needsEmbedding && !needsVault) return;
        let cancelled = false;
        const hydrate = async () => {
            try {
                console.info('[Knowledge Base]', '[app]', { event: 'workspace_overview_hydrate', id: overviewId });
                const [entityRaw, vaultRaw, embeddingRaw, storeRaw] = await Promise.all([
                    ingestionService.getKWEntityById(overviewId),
                    dataVaultService.getList(),
                    embeddingProviderService.getList(),
                    ingestionService.getVectorStores(),
                ]);
                if (cancelled) return;
                const entity = (entityRaw && typeof entityRaw === 'object' ? entityRaw : {}) as Record<string, unknown>;
                const vaults = asList(vaultRaw);
                const embeddings = asList(embeddingRaw);
                const stores = asList(storeRaw);
                const vaultKey = pickId(entity, 'vaultid', 'vaultId', 'VaultId');
                const embedKey = pickId(entity, 'embeddingModelId', 'EmbeddingModelId');
                const storeKey = pickId(entity, 'vectorstoreid', 'vectorStoreId', 'VectorStoreId');
                const vaultName = vaultKey
                    ? pickText(vaults.find((item) => pickId(item, 'id', 'Id') === vaultKey) || {}, 'name', 'Name', 'description', 'Description')
                    : '';
                let embeddingName = '';
                if (embedKey) {
                    const provider = embeddings.find((item) => pickId(item, 'id', 'Id') === embedKey);
                    embeddingName = provider ? embeddingOptionLabel(provider as unknown as EmbeddingProvider) : '';
                }
                if (!embeddingName && storeKey) {
                    const store = stores.find((item) => pickId(item, 'id', 'Id') === storeKey);
                    const defaultEmbed = pickId(store || {}, 'defaultEmbeddingProvider', 'DefaultEmbeddingProvider');
                    const provider = embeddings.find((item) => pickId(item, 'id', 'Id') === defaultEmbed);
                    embeddingName = provider ? embeddingOptionLabel(provider as unknown as EmbeddingProvider) : '';
                }
                if (!vaultName && !embeddingName) return;
                setWorkspaces((prev) => prev.map((item) => (
                    item.id === overviewId
                        ? {
                            ...item,
                            vaultId: item.vaultId || vaultName,
                            embeddingModel: item.embeddingModel || embeddingName,
                        }
                        : item
                )));
            } catch (err) {
                console.warn('[Knowledge Base]', '[warning]', {
                    event: 'workspace_overview_hydrate_failed',
                    message: err instanceof Error ? err.message : String(err),
                });
            }
        };
        void hydrate();
        return () => {
            cancelled = true;
        };
    }, [overviewId]);

    const filterCounts = useMemo(() => ({
        all: workspaces.length,
        vector: workspaces.filter((item) => destinationOf(item) === 'vector').length,
        vault: workspaces.filter((item) => destinationOf(item) === 'vault').length,
        other: workspaces.filter((item) => destinationOf(item) === 'other').length,
    }), [workspaces]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageStart = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
    const pageEnd = Math.min(safePage * PAGE_SIZE, filtered.length);
    const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const filterLabel =
        destFilter === 'vector' ? 'Vector store' : destFilter === 'vault' ? 'Data vault' : destFilter === 'other' ? 'Other' : 'All destinations';

    const handleDelete = async () => {
        if (!workspaceToDelete) return;
        const token = localStorage.getItem('token');
        const userInfo = token ? getUserInfo(token) : null;
        if (!userInfo) {
            console.warn('[Knowledge Base]', '[warning]', { event: 'workspace_delete_blocked_no_user' });
            return;
        }
        try {
            console.info('[Knowledge Base]', '[app]', { event: 'workspace_delete_submit', id: workspaceToDelete.id });
            const result = await ingestionService.deleteKBEntityGroup(workspaceToDelete.id, userInfo.sub);
            if (result && result.status === false) {
                const message = result.Message || result.message || 'Failed to delete workspace.';
                console.warn('[Knowledge Base]', '[warning]', { event: 'workspace_delete_rejected', message });
                alert(message);
                return;
            }
            console.info('[Knowledge Base]', '[app]', { event: 'workspace_delete_ok', id: workspaceToDelete.id });
            setWorkspaceToDelete(null);
            fetchWorkspaces();
        } catch (err) {
            console.error('[Knowledge Base]', '[error]', {
                event: 'workspace_delete_failed',
                message: err instanceof Error ? err.message : String(err),
            });
            alert('Failed to delete workspace.');
        }
    };


    const handleManage = (workspace: KnowledgeWorkspace) => {
        setSelectedWorkspace(workspace);
        setShowKBModal(true);
    };

    const handleCloseKBModal = () => {
        setShowKBModal(false);
        setSelectedWorkspace(null);
        openedDeepLinkRef.current = null;
        if (deepLinkWorkspaceId || deepLinkKbId) {
            navigate('/portal/knowledge-base', { replace: true });
        }
    };

    useEffect(() => {
        if (!deepLinkWorkspaceId || loading) return;
        if (openedDeepLinkRef.current === deepLinkWorkspaceId) return;

        const workspace = workspaces.find((w) => w.id === deepLinkWorkspaceId);
        if (workspace) {
            openedDeepLinkRef.current = deepLinkWorkspaceId;
            setSelectedWorkspace(workspace);
            setShowKBModal(true);
        }
    }, [deepLinkWorkspaceId, workspaces, loading]);

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);

    const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
    const menuItem = (active: boolean) =>
        `w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${
            active
                ? isLight
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'bg-indigo-500/15 text-indigo-300'
                : isLight
                    ? 'text-neutral-700 hover:bg-neutral-50'
                    : 'text-neutral-300 hover:bg-neutral-800'
        }`;
    const pillBtn = (active?: boolean) =>
        `inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${
            active
                ? isLight
                    ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                    : 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300'
                : isLight
                    ? 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
                    : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
        }`;

    const openAdd = () => setShowEntityModal(true);

    return (
        <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
            <header className="px-6 pt-6">
                <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
                    Configurations
                </p>
                <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>
                    Knowledge Workspace
                </h1>
                <p className={`mt-1.5 max-w-xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>
                    Group, manage, and configure your entities here.
                </p>
            </header>

            <div className="px-6 py-5 space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
                        <input
                            className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
                            placeholder="Search workspaces…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="relative" data-menu-root="filters">
                        <button type="button" className={pillBtn(destFilter !== 'all')} onClick={() => setFiltersOpen((value) => !value)}>
                            <SlidersHorizontal size={14} />
                            {filterLabel}
                            <ChevronDown size={13} />
                        </button>
                        <Menu open={filtersOpen} onClose={() => setFiltersOpen(false)} isLight={isLight} align="left" root="filters">
                            {([
                                ['all', 'All destinations', filterCounts.all],
                                ['vector', 'Vector store', filterCounts.vector],
                                ['vault', 'Data vault', filterCounts.vault],
                                ['other', 'Other', filterCounts.other],
                            ] as const).map(([id, label, count]) => (
                                <button
                                    key={id}
                                    type="button"
                                    className={menuItem(destFilter === id)}
                                    onClick={() => {
                                        setDestFilter(id);
                                        setFiltersOpen(false);
                                    }}
                                >
                                    {label}
                                    <span className="tabular-nums text-[11px] opacity-60">{count}</span>
                                </button>
                            ))}
                        </Menu>
                    </div>
                    <div className={`inline-flex items-center gap-1 text-sm ${muted}`}>
                        <span className="tabular-nums px-1">
                            {pageStart}–{pageEnd} of {filtered.length}
                        </span>
                        <button
                            type="button"
                            className={pillBtn()}
                            disabled={safePage <= 1}
                            onClick={() => setPage((value) => Math.max(1, value - 1))}
                            aria-label="Previous page"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <button
                            type="button"
                            className={pillBtn()}
                            disabled={safePage >= pageCount}
                            onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
                            aria-label="Next page"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                    <div className="relative ml-auto" data-menu-root="sort">
                        <button type="button" className={pillBtn()} onClick={() => setSortOpen((value) => !value)}>
                            {sort === 'name-desc' ? 'Name Z–A' : 'Name A–Z'}
                            <ChevronDown size={13} />
                        </button>
                        <Menu open={sortOpen} onClose={() => setSortOpen(false)} isLight={isLight} root="sort">
                            <button
                                type="button"
                                className={menuItem(sort === 'name-asc')}
                                onClick={() => {
                                    setSort('name-asc');
                                    setSortOpen(false);
                                }}
                            >
                                Name A–Z
                            </button>
                            <button
                                type="button"
                                className={menuItem(sort === 'name-desc')}
                                onClick={() => {
                                    setSort('name-desc');
                                    setSortOpen(false);
                                }}
                            >
                                Name Z–A
                            </button>
                        </Menu>
                    </div>
                    <button type="button" className={`${primaryBtn()} rounded-full shrink-0`} onClick={openAdd}>
                        <Plus size={14} /> Add New
                    </button>
                </div>

                {loading ? (
                    <div className={`flex flex-col items-center justify-center py-16 ${muted}`} aria-busy="true">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mb-3" />
                        <span className="text-sm">Loading workspaces…</span>
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        isLight={isLight}
                        title={workspaces.length === 0 ? 'No workspaces found' : 'No workspaces in this view'}
                        description={
                            workspaces.length === 0
                                ? 'Create a knowledge workspace to group and manage entities.'
                                : 'Try a different search or destination filter.'
                        }
                        action={
                            workspaces.length === 0 ? (
                                <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openAdd}>
                                    <Plus size={14} /> Add New
                                </button>
                            ) : undefined
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {pageItems.map((workspace) => (
                            <WorkspaceCard
                                key={workspace.id}
                                workspace={workspace}
                                isLight={isLight}
                                overviewOpen={overviewId === workspace.id}
                                onOpen={() => handleManage(workspace)}
                                onOverview={() => setOverviewId((current) => current === workspace.id ? null : workspace.id)}
                                onRename={() => {
                                    setRenameTarget({ id: workspace.id, name: workspace.title });
                                    setShowRenameModal(true);
                                }}
                                onDelete={() => setWorkspaceToDelete(workspace)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showEntityModal && (
                <KnowledgeEntityModal
                    tenantId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    userId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    onClose={() => setShowEntityModal(false)}
                    onSubmit={(data) => {
                        fetchWorkspaces()
                    }}
                />
            )}

            {showKBModal && selectedWorkspace && (
                <KnowledgebaseManagementModal
                    workspaceid={selectedWorkspace.id}
                    kbId={deepLinkKbId}
                    onClose={handleCloseKBModal}
                />
            )}

            {workspaceToDelete != null && (
                <ConfirmDialog
                    title="Delete knowledge workspace"
                    message={`This will delete "${workspaceToDelete.title}" and its knowledge bases, including linked vector collections. Type delete me to confirm.`}
                    confirmPhrase="delete me"
                    onYes={handleDelete}
                    onCancel={() => setWorkspaceToDelete(null)}
                />
            )}


            {renameTarget && showRenameModal && (
                <RenameModal
                    id={renameTarget.id}
                    currentName={renameTarget.name}
                    module="Knowledge Workspace"
                    onCancel={() => {
                        setShowRenameModal(false);
                        setRenameTarget(null);
                    }}
                    onSave={({ id, newName }) => {
                        knowledgebaseService.renameSource({
                            knowledgeBaseId: id,
                            tenantID: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
                            newTitle: newName,
                        }).then(() => {
                            fetchWorkspaces();
                            setShowRenameModal(false);
                            setRenameTarget(null);
                        });
                    }}

                />
            )}


        </div>
    );
};

export default DataSourceListingPage;
